import { useState } from 'react';
import api from '../utils/api';

export default function PurchaseOrderCreateDrawer({ workOrder, onClose, onSaved }) {
  const [form, setForm] = useState({
    po_number: '', period_start: '', period_end: '', approved_hours: '', timesheet_hours: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  // Business Consulting: the client's PO is optional and only a reference
  // (number + period) that invoices can be linked to - no hours or rates.
  const isConsulting = workOrder.engagement_type === 'business_consulting';

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleSave() {
    if (!form.po_number || !form.period_start || !form.period_end) {
      setError('PO number, period start, and period end are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const r = await api.post('/purchase-orders', {
        ...form,
        wo_id: workOrder.id,
        approved_hours: form.approved_hours ? Number(form.approved_hours) : null,
        timesheet_hours: form.timesheet_hours ? Number(form.timesheet_hours) : null,
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create Purchase Order');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="edit-drawer">
        <div className="drawer-header">
          <h3 className="drawer-title">{isConsulting ? 'Add client PO' : 'New Purchase Order'}</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        <div className="drawer-body">
          <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 20, fontSize: 13 }}>
            For Work Order <strong>{workOrder.client_wo_number}</strong> ({workOrder.engagement_type?.replace(/_/g, ' ')})
            <br />{isConsulting
              ? 'Optional - only when the client has issued a PO. Consulting invoices can be linked to it; several invoices may share one PO.'
              : "Rates will be locked from the Work Order's current rates at the moment this PO is created."}
          </div>

          {error && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 18 }}>
              {error}
            </div>
          )}

          {result && (
            <div style={{ background: 'var(--success-bg)', color: 'var(--success-text)', padding: '14px', borderRadius: 'var(--radius)', fontSize: 14, marginBottom: 18 }}>
              {isConsulting ? 'Client PO added.' : <>Created — locked sell rate ${result.sell_rate}/hr
              {result.buy_rate ? `, buy rate $${result.buy_rate}/hr` : ''}
              {result.commission_amount ? `, commission $${result.commission_amount.toFixed(2)}` : ''}.</>}
            </div>
          )}

          {!result && (
            <div className="form-grid">
              <div className="form-field span-2">
                <label>PO number</label>
                <input value={form.po_number} onChange={e => set('po_number', e.target.value)} autoFocus />
              </div>
              <div className="form-field">
                <label>Period start</label>
                <input type="date" value={form.period_start} onChange={e => set('period_start', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Period end</label>
                <input type="date" value={form.period_end} onChange={e => set('period_end', e.target.value)} />
              </div>
              {!isConsulting && <>
              <div className="form-field">
                <label>Approved hours</label>
                <input type="number" step="0.01" value={form.approved_hours} onChange={e => set('approved_hours', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Timesheet hours</label>
                <input type="number" step="0.01" value={form.timesheet_hours} onChange={e => set('timesheet_hours', e.target.value)} />
              </div>
              </>}
              <div className="form-field span-2">
                <label>Notes</label>
                <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <div className="drawer-footer">
          {result ? (
            <button className="btn btn-primary" onClick={() => { onSaved(); onClose(); }}>Done</button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Creating…' : isConsulting ? 'Add client PO' : 'Create Purchase Order'}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
